import { useCallback, useEffect, useRef, useState } from "react";
import {
  useHelpCenterStore,
  translateHelpCenterText,
} from "../../../Stores/HelpCenter";
import { useConfigStore } from "../../../Stores/Config";
import { ReactComponent as MagnifyingGlass } from "../../../Assets/magnifying-glass-solid.svg";
import { ArticleItem, Loading } from "../HelpCenterShared/HelpCenterShared";
import { focusWithoutScroll } from "../../../Helper/ScrollHelper";
import "./HelpCenterSearch.scss";

export function SearchView({ term }: { term: string }) {
  const searchResults = useHelpCenterStore((s) => s.searchResults);
  const searchTerm = useHelpCenterStore((s) => s.searchTerm);
  const searchArticles = useHelpCenterStore((s) => s.searchArticles);
  const clearSearch = useHelpCenterStore((s) => s.clearSearch);
  const searchLoading = useHelpCenterStore((s) => s.searchLoading);
  useConfigStore(); // re-render on config (language / translations) changes
  const [inputValue, setInputValue] = useState(term || "");
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Replaces the native `autoFocus` attribute, which would scroll the host page
  // (cross-frame focus scroll on Safari).
  useEffect(() => {
    focusWithoutScroll(searchInputRef.current);
  }, []);

  useEffect(() => {
    if (term && term.trim()) {
      setInputValue(term);
      searchArticles(term.trim());
    }
  }, [term, searchArticles]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setInputValue(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (!value.trim()) {
        clearSearch();
        return;
      }
      debounceRef.current = setTimeout(() => {
        searchArticles(value.trim());
      }, 300);
    },
    [searchArticles, clearSearch]
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const hasResults = searchResults && searchResults.length > 0;
  // The store sets `searchTerm` when a request actually fires, so this is only
  // true once results reflect the current input — not during the debounce while
  // the user is still typing. Gates the "nothing found" state so it never flashes
  // mid-typing; the previous content stays until a real search settles.
  const searchSettled = searchTerm === inputValue.trim();

  return (
    <>
      <div className="helpcenter-search-bar">
        <div className="helpcenter-search-bar-input-wrapper">
          <MagnifyingGlass />
          <input
            ref={searchInputRef}
            type="text"
            aria-label={translateHelpCenterText("searchPlaceholder")}
            placeholder={translateHelpCenterText("searchPlaceholder")}
            value={inputValue}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>
      </div>
      <div className="helpcenter-search-results">
        {inputValue.trim() && (
          <div className="helpcenter-search-results-header">
            <div
              className="helpcenter-search-results-header-title"
              role="heading"
              aria-level={2}
            >
              {translateHelpCenterText("searchResultsTitle")}:{" "}
              <span>{searchTerm || inputValue}</span>
            </div>
          </div>
        )}
        {searchLoading ? (
          <Loading />
        ) : hasResults ? (
          <div className="helpcenter-search-results-list">
            {searchResults.map((result: any) => {
              const article = result.document ?? result;
              return (
                <ArticleItem
                  key={article.id ?? article.docId}
                  article={article}
                />
              );
            })}
          </div>
        ) : (
          searchSettled &&
          inputValue.trim() && (
            <div className="helpcenter-search-results-empty">
              {translateHelpCenterText("noSearchResults")}
            </div>
          )
        )}
      </div>
    </>
  );
}
